import "./LanguageAltInput.css";

export type AyeInputLanguage = "yo" | "ha" | "ig" | "en";

export const AYESEARCH_LANGUAGE_STORAGE_KEY = "ayesearch-home-language";

export const AYE_INPUT_LANGUAGES: Array<{
  id: AyeInputLanguage;
  name: string;
  shortName: string;
}> = [
  { id: "yo", name: "Yorùbá", shortName: "YOR" },
  { id: "ha", name: "Hausa", shortName: "HAU" },
  { id: "ig", name: "Igbo", shortName: "IGB" },
  { id: "en", name: "English", shortName: "ENG" },
];

type LanguageMap = Record<string, string[]>;

const LANGUAGE_MAPS: Record<AyeInputLanguage, LanguageMap> = {
  yo: {
    a: ["á", "à", "ã", "ā"],
    e: ["ẹ", "é", "è", "ẹ́", "ẹ̀"],
    i: ["í", "ì"],
    o: ["ọ", "ó", "ò", "ọ́", "ọ̀"],
    u: ["ú", "ù"],
    m: ["ḿ", "m̀"],
    n: ["ń", "ǹ"],
    s: ["ṣ"],
  },
  ha: {
    d: ["ɗ"],
    k: ["ƙ"],
    h: ["ḥ"],
    r: ["ṛ"],
    s: ["ṣ"],
    t: ["ṭ"],
    y: ["ƴ"],
  },
  ig: {
    i: ["ị", "í", "ì"],
    o: ["ọ", "ó", "ò"],
    u: ["ụ", "ú", "ù"],
    n: ["ṅ", "ń", "ǹ"],
    m: ["ṁ", "ḿ", "m̀"],
  },
  en: {},
};

export function readAyeInputLanguage(): AyeInputLanguage {
  try {
    const stored = window.localStorage.getItem(AYESEARCH_LANGUAGE_STORAGE_KEY);
    if (stored === "yo" || stored === "ha" || stored === "ig" || stored === "en") {
      return stored;
    }
  } catch {
    // Restricted/private contexts may deny localStorage.
  }
  return "yo";
}

export function writeAyeInputLanguage(language: AyeInputLanguage): void {
  try {
    window.localStorage.setItem(AYESEARCH_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // The current page can still use the selected language in memory.
  }
  window.dispatchEvent(
    new CustomEvent<AyeInputLanguage>("ayesearch-language-change", {
      detail: language,
    }),
  );
}

export function getAyeInputLanguageMeta(language: AyeInputLanguage) {
  return AYE_INPUT_LANGUAGES.find((item) => item.id === language) ?? AYE_INPUT_LANGUAGES[0];
}

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export interface LanguageAltInputOptions {
  getLanguage?: () => AyeInputLanguage;
  popupLabel?: boolean;
}


export function openAyeLanguageTutorial(
  language: AyeInputLanguage = readAyeInputLanguage(),
): void {
  document.getElementById("aye-language-tutorial-modal")?.remove();

  const tutorialLabels: Record<AyeInputLanguage, string> = {
    yo: "Yoruba",
    ha: "Hausa",
    ig: "Igbo",
    en: "English",
  };

  const label = tutorialLabels[language];
  const map = LANGUAGE_MAPS[language];
  const baseKeys = Object.keys(map).filter((key) => key.length === 1);

  const createKeyCap = (value: string): HTMLElement => {
    const key = document.createElement("span");
    key.className = "language-tutorial-keycap";
    key.textContent = value;
    return key;
  };

  const overlay = document.createElement("div");
  overlay.id = "aye-language-tutorial-modal";
  overlay.className = "language-tutorial-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", `${label} language keyboard tutorial`);

  const modal = document.createElement("div");
  modal.className = "language-tutorial-modal";

  const header = document.createElement("div");
  header.className = "language-tutorial-header";

  const headingWrap = document.createElement("div");
  headingWrap.className = "language-tutorial-heading-wrap";

  const eyebrow = document.createElement("div");
  eyebrow.className = "language-tutorial-eyebrow";
  eyebrow.textContent = `${label} typing guide`;

  const title = document.createElement("h2");
  title.className = "language-tutorial-title";
  title.textContent = "Type language characters without leaving the keyboard";

  const subtitle = document.createElement("p");
  subtitle.className = "language-tutorial-subtitle";
  subtitle.textContent =
    language === "en"
      ? "English uses the normal keyboard. Choose Yoruba, Hausa, or Igbo to enable AyeSearch Alt character shortcuts."
      : "Hold either Alt key and press a supported letter. Keep Alt held and press the same letter again to cycle, or press the number shown beside a character to jump straight to it. Add Shift for uppercase.";

  headingWrap.append(eyebrow, title, subtitle);

  const close = document.createElement("button");
  close.type = "button";
  close.className = "language-tutorial-close";
  close.textContent = "×";
  close.setAttribute("aria-label", "Close tutorial");

  header.append(headingWrap, close);

  const demo = document.createElement("div");
  demo.className = "language-tutorial-demo";

  const demoTitle = document.createElement("div");
  demoTitle.className = "language-tutorial-demo-title";
  demoTitle.textContent = language === "en" ? "English keyboard mode" : "How cycling works";

  const demoKeys = document.createElement("div");
  demoKeys.className = "language-tutorial-demo-keys";

  if (language === "en") {
    demoKeys.append(createKeyCap("A"), document.createTextNode(" → A"));
  } else {
    demoKeys.append(
      createKeyCap("Alt"),
      document.createTextNode(" + "),
      createKeyCap(baseKeys[0]?.toUpperCase() || "A"),
    );
  }

  const demoText = document.createElement("div");
  demoText.className = "language-tutorial-demo-text";
  demoText.textContent =
    language === "en"
      ? "No custom AyeSearch character cycle is active while English is selected. Your physical keyboard input is entered normally."
      : "Each additional letter press cycles and replaces the previous character. While Alt stays held, press a displayed number to jump directly to that character. Releasing Alt ends the cycle.";

  demo.append(demoTitle, demoKeys, demoText);

  const grid = document.createElement("div");
  grid.className = "language-tutorial-grid";

  if (language === "en") {
    const card = document.createElement("div");
    card.className = "language-tutorial-card language-tutorial-card-wide";
    const copy = document.createElement("div");
    copy.className = "language-tutorial-english-copy";
    copy.textContent =
      "Select Yoruba, Hausa, or Igbo from the language selector whenever you want the floating Alt-character strip and numbered direct selection.";
    card.appendChild(copy);
    grid.appendChild(card);
  } else {
    baseKeys.forEach((baseKey) => {
      const variants = map[baseKey] ?? [];
      if (variants.length === 0) return;

      const card = document.createElement("div");
      card.className = "language-tutorial-card";

      const shortcut = document.createElement("div");
      shortcut.className = "language-tutorial-shortcut";

      const baseLabel = document.createElement("div");
      baseLabel.className = "language-tutorial-base";
      baseLabel.textContent = baseKey.toUpperCase();

      const keyLine = document.createElement("div");
      keyLine.className = "language-tutorial-keyline";
      keyLine.append(
        createKeyCap("Alt"),
        document.createTextNode(" + "),
        createKeyCap(baseKey.toUpperCase()),
      );

      shortcut.append(baseLabel, keyLine);

      const sequence = document.createElement("div");
      sequence.className = "language-tutorial-sequence";

      variants.forEach((variant, index) => {
        const value = document.createElement("span");
        value.className = "language-tutorial-variant";
        value.title = `Hold Alt and press ${index + 1}: ${variant}`;

        const character = document.createElement("span");
        character.className = "language-tutorial-variant-character";
        character.textContent = variant;

        const number = document.createElement("span");
        number.className = "language-tutorial-variant-number";
        number.textContent = String(index + 1);

        value.append(character, number);
        sequence.appendChild(value);

        if (index < variants.length - 1) {
          const arrow = document.createElement("span");
          arrow.className = "language-tutorial-arrow";
          arrow.textContent = "→";
          sequence.appendChild(arrow);
        }
      });

      const uppercase = document.createElement("div");
      uppercase.className = "language-tutorial-uppercase";
      uppercase.append(
        createKeyCap("Alt"),
        document.createTextNode(" + "),
        createKeyCap("Shift"),
        document.createTextNode(" + "),
        createKeyCap(baseKey.toUpperCase()),
      );

      const uppercaseValues = document.createElement("span");
      uppercaseValues.className = "language-tutorial-uppercase-values";
      uppercaseValues.textContent = variants
        .map((variant) => variant.toLocaleUpperCase())
        .join("  ");
      uppercase.appendChild(uppercaseValues);

      card.append(shortcut, sequence, uppercase);
      grid.appendChild(card);
    });
  }

  const footer = document.createElement("div");
  footer.className = "language-tutorial-footer";
  footer.textContent =
    language === "ha"
      ? "Hausa multi-letter entries such as ts, sh, kw, and gw are not Alt cycles; the Alt shortcuts use single physical letter keys. Avoid bare Alt, Alt + Space, and Alt + R because desktop software may capture them."
      : language === "en"
        ? "Tip: change the AyeSearch language selector to Yoruba, Hausa, or Igbo to activate language character shortcuts."
        : "Tip: the floating option strip shows the complete cycle. Keep Alt held and press its displayed number to jump directly to a character. Avoid bare Alt, Alt + Space, and Alt + R because desktop software may capture them.";

  modal.append(header, demo, grid, footer);
  overlay.appendChild(modal);

  const closeModal = () => {
    document.removeEventListener("keydown", onKeyDown);
    overlay.remove();
  };

  close.addEventListener("click", closeModal);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) closeModal();
  });

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
    }
  };

  document.addEventListener("keydown", onKeyDown);
  document.body.appendChild(overlay);
  close.focus({ preventScroll: true });
}

export function attachLanguageAltInput(
  element: InputElement,
  options: LanguageAltInputOptions = {},
): () => void {
  const getLanguage = options.getLanguage ?? readAyeInputLanguage;
  const ownerId = `aye-alt-owner-${Math.random().toString(36).slice(2)}`;
  element.dataset.ayeAltOwner = ownerId;

  let altLeftDown = false;
  let altRightDown = false;
  let cycleLanguage: AyeInputLanguage | null = null;
  let cycleKey: string | null = null;
  let cycleIndex = -1;
  let cycleStart: number | null = null;
  let cycleEnd: number | null = null;
  let cycleUppercase = false;

  const popup = document.createElement("div");
  popup.className = "aye-alt-language-popup";
  popup.dataset.owner = ownerId;
  popup.setAttribute("role", "status");
  popup.setAttribute("aria-live", "polite");
  popup.setAttribute("aria-hidden", "true");
  document.body.appendChild(popup);

  const hidePopup = () => {
    popup.classList.remove("visible");
    popup.setAttribute("aria-hidden", "true");
  };

  const resetCycle = () => {
    cycleLanguage = null;
    cycleKey = null;
    cycleIndex = -1;
    cycleStart = null;
    cycleEnd = null;
    cycleUppercase = false;
    hidePopup();
  };

  const dispatchInput = () => {
    element.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const replaceRange = (start: number, end: number, value: string) => {
    element.setRangeText(value, start, end, "end");
    cycleStart = start;
    cycleEnd = start + value.length;
    dispatchInput();
  };

  const positionPopup = () => {
    const rect = element.getBoundingClientRect();
    const caret = element.selectionStart ?? element.value.length;
    const ratio = element.value.length > 0 ? Math.min(1, caret / element.value.length) : 0.08;
    const estimatedX = rect.left + Math.max(28, Math.min(rect.width - 28, rect.width * ratio));

    popup.style.left = `${estimatedX}px`;
    popup.style.top = `${rect.bottom + 8}px`;
    popup.style.transform = "translateX(-50%)";

    requestAnimationFrame(() => {
      const popupRect = popup.getBoundingClientRect();
      let left = estimatedX;
      let top = rect.bottom + 8;

      if (popupRect.right > window.innerWidth - 10) {
        left -= popupRect.right - (window.innerWidth - 10);
      }
      if (popupRect.left < 10) {
        left += 10 - popupRect.left;
      }
      if (popupRect.bottom > window.innerHeight - 10 && rect.top > popupRect.height + 18) {
        top = rect.top - popupRect.height - 8;
      }

      popup.style.left = `${left}px`;
      popup.style.top = `${top}px`;
    });
  };

  const showPopup = (
    language: AyeInputLanguage,
    key: string,
    variants: string[],
    activeIndex: number,
    uppercase: boolean,
  ) => {
    popup.innerHTML = "";

    if (options.popupLabel !== false) {
      const label = document.createElement("span");
      label.className = "aye-alt-language-popup-label";
      label.textContent = `${getAyeInputLanguageMeta(language).name} · Alt + ${key.toUpperCase()}`;
      popup.appendChild(label);
    }

    const row = document.createElement("div");
    row.className = "aye-alt-language-popup-row";

    variants.forEach((variant, index) => {
      const item = document.createElement("span");
      item.className = "aye-alt-language-popup-option";
      if (index === activeIndex) item.classList.add("active");

      const character = document.createElement("span");
      character.className = "aye-alt-language-popup-character";
      character.textContent = uppercase ? variant.toLocaleUpperCase() : variant;

      const number = document.createElement("span");
      number.className = "aye-alt-language-popup-number";
      number.textContent = String(index + 1);

      item.append(character, number);
      row.appendChild(item);
    });

    popup.appendChild(row);
    popup.classList.add("visible");
    popup.setAttribute("aria-hidden", "false");
    positionPopup();
  };

  const onKeyDown: EventListener = (rawEvent) => {
    const event = rawEvent as KeyboardEvent;
    if (event.code === "AltLeft") {
      altLeftDown = true;
      event.preventDefault();
      return;
    }
    if (event.code === "AltRight") {
      altRightDown = true;
      event.preventDefault();
      return;
    }

    if (event.repeat) return;

    const altActive = altLeftDown || altRightDown || event.altKey;
    if (!altActive || event.metaKey) return;

    // Leave OS/browser-level combinations alone. They may be captured before
    // the page ever receives them, and the language helper should not pretend
    // it can own those shortcuts.
    if (event.code === "Space" || event.code === "KeyR") {
      resetCycle();
      return;
    }

    const digitMatch = /^Digit([1-9])$/.exec(event.code);
    if (digitMatch) {
      if (
        cycleLanguage &&
        cycleKey &&
        cycleStart !== null &&
        cycleEnd !== null
      ) {
        event.preventDefault();
        event.stopPropagation();
        const variants = LANGUAGE_MAPS[cycleLanguage][cycleKey] ?? [];
        const requestedIndex = Number(digitMatch[1]) - 1;

        if (requestedIndex >= 0 && requestedIndex < variants.length) {
          cycleIndex = requestedIndex;
          const raw = variants[cycleIndex];
          const replacement = cycleUppercase ? raw.toLocaleUpperCase() : raw;
          const start = cycleStart;
          const end = cycleEnd;
          replaceRange(start, end, replacement);
          showPopup(cycleLanguage, cycleKey, variants, cycleIndex, cycleUppercase);
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const keyMatch = /^Key([A-Z])$/.exec(event.code);
    if (!keyMatch) return;

    const language = getLanguage();
    const key = keyMatch[1].toLowerCase();
    const variants = LANGUAGE_MAPS[language][key] ?? [];
    if (!variants.length) {
      resetCycle();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const uppercase = event.shiftKey;
    const caretStart = element.selectionStart ?? element.value.length;
    const caretEnd = element.selectionEnd ?? caretStart;
    const sameCycle =
      cycleLanguage === language &&
      cycleKey === key &&
      cycleStart !== null &&
      cycleEnd !== null &&
      caretStart === cycleEnd &&
      caretEnd === cycleEnd;

    cycleLanguage = language;
    cycleKey = key;
    cycleUppercase = uppercase;

    if (sameCycle && cycleStart !== null && cycleEnd !== null) {
      cycleIndex = (cycleIndex + 1) % variants.length;
      const raw = variants[cycleIndex];
      const replacement = uppercase ? raw.toLocaleUpperCase() : raw;
      const start = cycleStart;
      const end = cycleEnd;
      replaceRange(start, end, replacement);
    } else {
      cycleIndex = 0;
      const raw = variants[0];
      const replacement = uppercase ? raw.toLocaleUpperCase() : raw;
      replaceRange(caretStart, caretEnd, replacement);
    }

    showPopup(language, key, variants, cycleIndex, uppercase);
  };

  const onKeyUp: EventListener = (rawEvent) => {
    const event = rawEvent as KeyboardEvent;
    if (event.code === "AltLeft") {
      altLeftDown = false;
      if (!altRightDown) resetCycle();
      return;
    }
    if (event.code === "AltRight") {
      altRightDown = false;
      if (!altLeftDown) resetCycle();
    }
  };

  const onBlur = () => {
    altLeftDown = false;
    altRightDown = false;
    resetCycle();
  };

  const onScrollOrResize = () => {
    if (popup.classList.contains("visible")) positionPopup();
  };

  element.addEventListener("keydown", onKeyDown);
  element.addEventListener("keyup", onKeyUp);
  element.addEventListener("blur", onBlur);
  element.addEventListener("scroll", onScrollOrResize);
  window.addEventListener("resize", onScrollOrResize);

  return () => {
    element.removeEventListener("keydown", onKeyDown);
    element.removeEventListener("keyup", onKeyUp);
    element.removeEventListener("blur", onBlur);
    element.removeEventListener("scroll", onScrollOrResize);
    window.removeEventListener("resize", onScrollOrResize);
    popup.remove();
  };
}
